import React from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";

export default function DisclaimerPage() {
  const navigate = useNavigate();

  const sections = [
    {
      id: "disclaimer-intro",
      title: "Important Disclaimer",
      content:
        "Please read this disclaimer carefully before using the Koin platform. By accessing and using Koin, you acknowledge and agree to this disclaimer and all terms outlined below.",
    },
    {
      id: "not-employment",
      title: "Not Employment or Income Guarantee",
      content:
        "Koin is a user acquisition and engagement platform. We do NOT provide employment, fixed income, investment opportunities, or financial services. Participation in campaigns does not constitute employment. Incentives are not guaranteed and depend entirely on successful completion and validation of campaign requirements.",
      details: [
        "No guaranteed income or minimum earnings",
        "No employment relationship or labor contract",
        "No benefits such as health insurance, paid leave, or pension",
        "Campaign completion does not guarantee incentive payout",
      ],
    },
    {
      id: "incentive-conditions",
      title: "Campaign & Incentive Conditions",
      content:
        "All incentives are conditional and subject to the following:",
      details: [
        "Successful completion of all campaign requirements as specified",
        "Validation and verification of submitted proof by our team",
        "Compliance with campaign guidelines and platform terms",
        "Absence of fraudulent or suspicious activity",
        "Passing anti-fraud checks and verification processes",
      ],
    },
    {
      id: "fraud-disqualification",
      title: "Fraud & Disqualification",
      content:
        "Koin reserves the right to disqualify users and reject incentive payouts for:",
      details: [
        "Fraudulent submissions or fake proof",
        "Multiple accounts from same device or person",
        "Attempts to manipulate or bypass verification",
        "Suspicious or anomalous activity patterns",
        "Violation of campaign guidelines or platform terms",
        "Use of automated tools or bots",
      ],
    },
    {
      id: "processing-times",
      title: "Payment Processing Timeline",
      content:
        "Incentive payouts are NOT instant. Processing timeline is as follows:",
      details: [
        "Campaign submissions submitted",
        "Verification review: 24-72 hours (may vary based on volume)",
        "Approval or rejection notification",
        "Payout processing: 1-2 business days after approval",
        "Total time from submission to receipt: 2-5 business days approximately",
        "No expedited or instant payout options available",
      ],
    },
    {
      id: "platform-liability",
      title: "Platform Liability & Support",
      content:
        "Koin's liability is limited as follows:",
      details: [
        "We are not liable for network issues, app crashes, or device-related problems",
        "We are not liable for third-party payment processor issues",
        "We are not liable for lost data or submission failures due to user actions",
        "We have no obligation to recover rejected submissions",
        "Support is provided on best-effort basis during business hours",
        "Response times may vary based on volume and complexity",
      ],
    },
    {
      id: "data-privacy",
      title: "Data & Privacy",
      content:
        "Please note:",
      details: [
        "Personal data is collected for campaign participation and payment processing",
        "Data is shared with payment processors and verification partners",
        "We do not guarantee absolute security or data protection",
        "Data may be disclosed if required by law or regulations",
        "See Privacy Policy for complete data handling practices",
      ],
    },
    {
      id: "terms-compliance",
      title: "Compliance & Legal",
      content:
        "By using Koin, you confirm:",
      details: [
        "You are 18 years of age or older",
        "You are responsible for all taxes on incentive income",
        "You comply with all applicable local and national laws",
        "You are not located in restricted jurisdictions",
        "You accept full responsibility for your participation and submissions",
      ],
    },
    {
      id: "changes",
      title: "Changes to Disclaimer",
      content:
        "Koin reserves the right to modify this disclaimer at any time. Continued use of the platform constitutes acceptance of updated terms.",
    },
    {
      id: "contact",
      title: "Questions About This Disclaimer",
      content:
        "If you have questions about this disclaimer or our policies, please contact us at support@koinapp.co",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <Container
        maxWidth="md"
        sx={{
          pt: 6,
          pb: 8,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium mb-4"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 16L6 10L12 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: "#1a1a1a",
            }}
          >
            Disclaimer
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              fontSize: "1.1rem",
            }}
          >
            Important information about using the Koin platform
          </Typography>
        </Box>

        {/* Sections */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {sections.map((section) => (
            <Box
              key={section.id}
              sx={{
                bgcolor: "#fff",
                p: 4,
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#EF6C00",
                }}
              >
                {section.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#333",
                  mb: section.details ? 2 : 0,
                  lineHeight: 1.7,
                }}
              >
                {section.content}
              </Typography>
              {section.details && (
                <ul
                  style={{
                    marginLeft: "20px",
                    color: "#555",
                    lineHeight: 1.8,
                  }}
                >
                  {section.details.map((detail, idx) => (
                    <li key={idx} style={{ marginBottom: "8px" }}>
                      {detail}
                    </li>
                  ))}
                </ul>
              )}
            </Box>
          ))}
        </Box>

        {/* Footer Note */}
        <Box
          sx={{
            mt: 6,
            p: 3,
            bgcolor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#856404",
              fontWeight: 600,
            }}
          >
            Last Updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
